import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { 
  ArrowRight, 
  Users, 
  Calendar, 
  ClipboardList, 
  Swords,
  Video,
  Check,
  Crown,
  Dumbbell,
  Star,
  Shield,
  ChevronDown,
  ChevronUp,
  Target,
  TrendingUp,
  Trophy,
  Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const features = [
  {
    icon: Users,
    title: 'Fighter Profiles',
    description: 'Track fighter stats, records, weight classes, and performance metrics in one place.'
  },
  {
    icon: Calendar,
    title: 'Training Camps',
    description: 'Plan and manage training camps with scheduling tools and progress tracking.'
  },
  {
    icon: ClipboardList,
    title: 'Assessments',
    description: 'Evaluate striking, grappling, wrestling, and cardio with detailed scoring systems.'
  },
  {
    icon: Swords,
    title: 'Fight Schedule',
    description: 'Organize upcoming bouts, track results, and manage your gym\'s competition calendar.'
  }
];

const faqs = [
  {
    question: "How long is the free trial?",
    answer: "Every plan includes a full 30-day free trial. No credit card required to start. You'll have access to all features during the trial period."
  },
  {
    question: "Can I switch plans later?",
    answer: "Absolutely! You can upgrade or downgrade your plan at any time. If you upgrade, you'll get immediate access to new features. Downgrades take effect at the next billing cycle."
  },
  {
    question: "Is my fighters' data secure?",
    answer: "Security is our top priority. We use bank-level encryption, secure authentication with rate limiting, and strict multi-tenant isolation. Your gym's data is completely separate from other gyms."
  },
  {
    question: "Can fighters access their own profiles?",
    answer: "Yes! With the Fighter Portal, your athletes can log in to view their stats, assessments, upcoming fights, and training schedules. You control what they can see and edit."
  },
  {
    question: "Do you support both MMA and BJJ?",
    answer: "Yes! Our BJJ plan includes specialized features like belt tracking, IBJJF-style competition records, and 8-category BJJ assessments. MMA gyms get 12-category assessments, fight tracking, and weight cut management."
  },
  {
    question: "What kind of support do you offer?",
    answer: "All plans include email support. We also have a comprehensive help center with guides and tutorials to help you get started."
  }
];

const basePricingTiers = [
  {
    id: 'bjj',
    name: 'BJJ',
    foundersPrice: '$129.99',
    regularPrice: '$199.99',
    period: '/month',
    description: 'For Brazilian Jiu-Jitsu Academies',
    icon: Dumbbell,
    color: 'blue',
    features: [
      '8-Category BJJ Assessments',
      'Belt & Stripe Tracking',
      'Competition Records',
      'Training Camps',
      'Fighter Portal Access',
      'Video Uploads',
      'Attendance Tracking',
      'Public Fighter Profiles'
    ]
  },
  {
    id: 'mma',
    name: 'MMA',
    foundersPrice: '$159.99',
    regularPrice: '$299.99',
    period: '/month',
    description: 'For Mixed Martial Arts Gyms',
    icon: Swords,
    color: 'red',
    featured: true,
    features: [
      '12-Category MMA Assessments',
      'Fight Scheduling & Records',
      'Weight Cut Management',
      'Training Camps',
      'Fighter Portal Access',
      'Video Uploads',
      'Fight Week Mode',
      'Post-Fight Analysis'
    ]
  },
  {
    id: 'hybrid',
    name: 'Hybrid',
    foundersPrice: '$199.99',
    regularPrice: '$399.99',
    period: '/month',
    description: 'Complete Solution for Multi-Sport Facilities',
    icon: Crown,
    color: 'yellow',
    features: [
      'All BJJ Features Included',
      'All MMA Features Included',
      'Multi-Sport Athlete Profiles',
      'Toggle Assessment Types',
      'Priority Support',
      'Advanced Analytics Dashboard',
      'Stripe Connect Payments',
      'API Access & Custom Branding'
    ]
  }
];

// FAQ Item Component
const FAQItem = ({ question, answer, index }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="border border-ba-border bg-ba-surface/50 rounded-sm overflow-hidden"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-ba-surface/80 transition-colors"
      >
        <span className="font-heading text-white text-sm uppercase tracking-wide">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-red-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-zinc-500 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="px-6 pb-4"
        >
          <p className="text-zinc-400 text-sm font-body leading-relaxed">{answer}</p>
        </motion.div>
      )}
    </motion.div>
  );
};

export const LandingPage = () => {
  const [foundersData, setFoundersData] = useState({
    isFounders: true,
    spotsRemaining: 25,
    spotsTotal: 25
  });

  useEffect(() => {
    const fetchFoundersStatus = async () => {
      try {
        const response = await axios.get(`${API}/api/founders/status`);
        setFoundersData({
          isFounders: response.data.is_active,
          spotsRemaining: response.data.spots_remaining,
          spotsTotal: response.data.spots_total
        });
      } catch (error) {
        console.error('Failed to fetch founders status:', error);
      }
    };
    fetchFoundersStatus();
  }, []);

  // Get price based on founders status
  const getPrice = (tier) => {
    return foundersData.isFounders ? tier.foundersPrice : tier.regularPrice;
  };

  return (
    <div className="min-h-screen bg-ba-black noise-overlay">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-ba-black/90 backdrop-blur-xl border-b border-red-600/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_mma-gym-portal/artifacts/nfeq9dhc_file_00000000262c71fd9b8a476a6aaafaf3.png"
                alt="Black Arrow MMA"
                className="h-10 w-auto"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-zinc-400 hover:text-white font-body">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider transition-all" data-testid="get-started-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-screen flex items-center">
        {/* Background with logo */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-ba-black/90 via-ba-black/70 to-ba-black z-10" />
          <img
            src="https://customer-assets.emergentagent.com/job_mma-gym-portal/artifacts/nfeq9dhc_file_00000000262c71fd9b8a476a6aaafaf3.png"
            alt="Black Arrow MMA"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        
        {/* Red glow effects */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-red-600/20 rounded-full blur-[150px] z-5" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-red-600/10 rounded-full blur-[100px] z-5" />
        
        <div className="relative z-20 max-w-7xl mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[10px] tracking-[0.4em] uppercase text-red-500 font-body mb-4 flex items-center gap-2">
                <span className="w-8 h-px bg-red-600"></span>
                Professional Fighter Management
              </p>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[0.9] mb-4">
                FORGE YOUR<br />
                <span className="text-red-600">CHAMPIONS</span>
              </h1>
              
              {/* Tagline */}
              <div className="mb-8 flex items-center gap-3">
                <span className="w-12 h-px bg-gradient-to-r from-red-600 to-transparent"></span>
                <p className="font-heading text-lg sm:text-xl text-zinc-300 tracking-wide italic">
                  "Precise Preparation <span className="text-red-500">/</span> Elite Execution"
                </p>
              </div>
              
              <p className="text-lg sm:text-xl text-zinc-400 mb-8 max-w-xl font-body leading-relaxed">
                The ultimate fighter development system for MMA gyms. Track progress, manage training camps, and build champions from the ground up.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider h-14 px-8 transition-all" data-testid="hero-get-started-btn">
                    Start Free Trial
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#pricing">
                  <Button size="lg" variant="outline" className="border-red-600/50 text-white hover:bg-red-600/10 hover:border-red-600 uppercase font-heading tracking-wider h-14 px-8 transition-all">
                    View Pricing
                  </Button>
                </a>
              </div>
            </motion.div>
            
            {/* Logo Display */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:flex justify-center items-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_mma-gym-portal/artifacts/nfeq9dhc_file_00000000262c71fd9b8a476a6aaafaf3.png"
                alt="Black Arrow MMA Logo"
                className="max-w-md w-full drop-shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-ba-surface/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-3">Platform Features</p>
            <h2 className="font-heading text-5xl sm:text-6xl text-white tracking-tight mb-4">
              EVERYTHING YOU NEED
            </h2>
            <p className="text-zinc-500 text-lg max-w-2xl mx-auto font-body">
              Comprehensive tools designed for serious MMA training facilities
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-ba-surface border border-ba-border p-6 rounded-sm hover:border-red-600/50 hover:shadow-red-glow transition-all group"
              >
                <div className="w-12 h-12 bg-red-600/10 border border-red-600/20 rounded-sm flex items-center justify-center mb-4 group-hover:bg-red-600/20 group-hover:border-red-600/40 transition-all">
                  <feature.icon className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="font-heading text-2xl text-white tracking-tight mb-2">
                  {feature.title.toUpperCase()}
                </h3>
                <p className="text-zinc-500 text-sm font-body leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Feature */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-5 h-5 text-yellow-500" />
                <span className="text-yellow-500 uppercase text-[10px] tracking-[0.2em] font-body">Gold Feature</span>
              </div>
              <h2 className="font-heading text-5xl sm:text-6xl text-white tracking-tight mb-4">
                TRAINING VIDEO<br />
                <span className="text-red-600">UPLOADS</span>
              </h2>
              <p className="text-zinc-500 text-lg mb-6 font-body leading-relaxed">
                Upload and organize training footage for each fighter. Review techniques, analyze sparring sessions, and track improvement over time with cloud-based video storage.
              </p>
              <ul className="space-y-3">
                {['Secure cloud storage', 'Fighter-specific organization', 'Easy sharing with coaches'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-zinc-300">
                    <div className="w-5 h-5 rounded-full bg-red-600/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-red-500" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-tr from-red-600/20 to-transparent rounded-sm" />
              <img
                src="https://images.unsplash.com/photo-1561532325-7d5231a2dede?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Training"
                className="rounded-sm border border-zinc-800"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-red-600/90 rounded-full flex items-center justify-center">
                  <Video className="w-8 h-8 text-white" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-ba-surface/50">
        <div className="max-w-7xl mx-auto">
          {/* Founders Banner */}
          {foundersData.isFounders && foundersData.spotsRemaining > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12 p-6 rounded-lg bg-gradient-to-r from-yellow-600/20 via-yellow-500/10 to-yellow-600/20 border border-yellow-600/50 max-w-4xl mx-auto"
            >
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-600/20 rounded-full">
                    <Trophy className="w-8 h-8 text-yellow-500" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-yellow-500 uppercase tracking-tight flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      Founding Member Pricing
                    </h3>
                    <p className="text-zinc-300">
                      Lock in exclusive lifetime rates - limited to first 25 gyms!
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-yellow-500">
                    {foundersData.spotsRemaining}
                  </div>
                  <div className="text-sm text-zinc-400">spots left</div>
                </div>
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-3">
              {foundersData.isFounders ? '🏆 Founders Pricing' : 'Pricing Plans'}
            </p>
            <h2 className="font-heading text-5xl sm:text-6xl text-white tracking-tight mb-4">
              {foundersData.isFounders ? 'FOUNDERS PRICING' : 'SIMPLE PRICING'}
            </h2>
            <p className="text-zinc-500 text-lg max-w-2xl mx-auto font-body">
              {foundersData.isFounders 
                ? 'Be one of our first 25 gyms and lock in exclusive lifetime rates!'
                : 'Choose the plan that fits your gym\'s needs'
              }
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {basePricingTiers.map((tier, index) => {
              const Icon = tier.icon;
              const borderColor = tier.featured 
                ? 'border-red-600 shadow-red-glow-lg' 
                : tier.color === 'blue' 
                  ? 'border-blue-600/50 hover:border-blue-600' 
                  : tier.color === 'yellow' 
                    ? 'border-yellow-600/50 hover:border-yellow-600'
                    : 'border-ba-border hover:border-red-600/30';
              const iconBgColor = tier.color === 'blue' 
                ? 'bg-blue-600/20' 
                : tier.color === 'yellow' 
                  ? 'bg-yellow-600/20' 
                  : 'bg-red-600/20';
              const iconColor = tier.color === 'blue' 
                ? 'text-blue-500' 
                : tier.color === 'yellow' 
                  ? 'text-yellow-500' 
                  : 'text-red-500';
              const checkColor = tier.color === 'blue'
                ? 'bg-blue-600/20 border-blue-600/30 text-blue-500'
                : tier.color === 'yellow'
                  ? 'bg-yellow-600/20 border-yellow-600/30 text-yellow-500'
                  : 'bg-red-600/20 border-red-600/30 text-red-500';
              
              return (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative p-6 rounded-sm border bg-ba-surface/50 transition-all ${borderColor}`}
              >
                {tier.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-red-600 text-white text-[10px] uppercase tracking-[0.15em] font-body px-4 py-1.5 rounded-sm shadow-red-glow flex items-center gap-1.5">
                      <Star className="w-3 h-3" />
                      Most Popular
                    </span>
                  </div>
                )}
                {foundersData.isFounders && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-yellow-600 text-black text-[9px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm flex items-center gap-1">
                      <Trophy className="w-3 h-3" />
                      Founders
                    </span>
                  </div>
                )}
                <div className="mb-6 text-center">
                  <div className={`w-14 h-14 mx-auto mb-4 rounded-sm ${iconBgColor} flex items-center justify-center`}>
                    <Icon className={`w-7 h-7 ${iconColor}`} />
                  </div>
                  <h3 className="font-heading text-2xl text-white tracking-tight mb-1">
                    {tier.name.toUpperCase()}
                  </h3>
                  <p className="text-zinc-500 text-sm font-body mb-4">{tier.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-heading text-4xl text-white">{getPrice(tier)}</span>
                    <span className="text-zinc-500 font-body text-sm">{tier.period}</span>
                  </div>
                  {foundersData.isFounders && (
                    <div className="mt-2">
                      <span className="text-zinc-500 line-through text-sm">{tier.regularPrice}/mo</span>
                      <span className="text-yellow-500 text-xs ml-2">Save {Math.round((1 - parseFloat(tier.foundersPrice.replace('$','')) / parseFloat(tier.regularPrice.replace('$',''))) * 100)}%</span>
                    </div>
                  )}
                </div>
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-zinc-400 font-body text-sm">
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 ${checkColor}`}>
                        <Check className="w-3 h-3" />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to={`/register?plan=${tier.name.toLowerCase()}`}>
                  <Button 
                    className={`w-full h-12 uppercase font-heading font-bold tracking-wide text-sm ${
                      tier.featured 
                        ? 'bg-red-600 hover:bg-red-700 text-white shadow-red-glow' 
                        : tier.color === 'blue'
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : tier.color === 'yellow'
                            ? 'bg-yellow-600 hover:bg-yellow-700 text-black'
                            : 'bg-zinc-800 hover:bg-zinc-700 text-white'
                    }`}
                    data-testid={`pricing-${tier.name.toLowerCase()}-btn`}
                  >
                    Start Free Trial
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </motion.div>
              );
            })}
          </div>
          <p className="text-center text-zinc-500 text-sm mt-8 font-body">
            All plans include a 30-day free trial. No credit card required.
          </p>
        </div>
      </section>

      {/* Simple Trust Indicators */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-y border-ba-border bg-ba-surface/30">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            <div className="flex items-center gap-2 text-zinc-500">
              <Shield className="w-5 h-5 text-green-500" />
              <span className="text-sm font-body">Secure & Encrypted</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-500">
              <Target className="w-5 h-5 text-red-500" />
              <span className="text-sm font-body">Built for Fighters</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-500">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              <span className="text-sm font-body">Track Progress Over Time</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-ba-surface/50">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-3">Got Questions?</p>
            <h2 className="font-heading text-5xl text-white tracking-tight mb-4">
              FAQ
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-ba-black to-zinc-900/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-3">On The Horizon</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-white tracking-tight">
              COMING <span className="text-red-600">SOON</span>
            </h2>
            <p className="text-zinc-500 mt-4 max-w-2xl mx-auto font-body">
              We're constantly building new features to help you develop champions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Promoter Tools",
                description: "Direct integration with promoters for fight bookings and event management",
                icon: "🎯"
              },
              {
                title: "Sponsor & Fighter Finder",
                description: "Connect fighters with sponsors and help promoters discover talent",
                icon: "🤝"
              },
              {
                title: "Manager Tools",
                description: "Comprehensive management suite for fighter managers and agents",
                icon: "📋"
              },
              {
                title: "Advanced Video Analysis",
                description: "AI-powered breakdown with telestrator tools for technique analysis",
                icon: "🎬"
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-ba-surface/50 border border-dashed border-red-600/30 rounded-sm p-6 relative overflow-hidden group hover:border-red-600/50 transition-all"
              >
                <div className="absolute top-3 right-3">
                  <span className="px-2 py-1 bg-red-600/20 text-red-500 text-[10px] uppercase tracking-wider rounded-sm font-body">
                    Coming Soon
                  </span>
                </div>
                <div className="text-3xl mb-4">{feature.icon}</div>
                <h3 className="font-heading text-lg text-white mb-2">{feature.title}</h3>
                <p className="text-zinc-500 text-sm font-body">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 font-heading text-[30vw] text-white/[0.01] pointer-events-none select-none whitespace-nowrap flex items-center justify-center">
          CHAMPION
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-3">Get Started Today</p>
            <h2 className="font-heading text-5xl sm:text-6xl text-white tracking-tight mb-6">
              READY TO <span className="text-red-600">ELEVATE</span><br />
              YOUR GYM?
            </h2>
            <p className="text-zinc-500 text-lg mb-8 max-w-2xl mx-auto font-body">
              Join the growing number of MMA gyms using Black Arrow to develop champions.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider h-14 px-8 transition-all" data-testid="cta-get-started-btn">
                Start Your Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-ba-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-600 rounded-sm flex items-center justify-center shadow-red-glow">
              <span className="font-heading text-white">BA</span>
            </div>
            <span className="text-zinc-500 font-body text-sm">© 2024 Black Arrow MMA. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-6 text-zinc-500 text-sm font-body">
            <Link to="/privacy" className="hover:text-red-500 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-red-500 transition-colors">Terms</Link>
            <a href="mailto:support@blackarrowmma.com" className="hover:text-red-500 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
